import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { logGenerationError } from '../lib/errorLogger';
import { FocusHeader } from '../components/FocusHeader';
import { StepProgressIndicator, ProgressStep } from '../components/StepProgressIndicator';
import { WIZARD_PATHS, wizardUrl } from '../lib/wizardRoutes';
import { useCreditsCheck } from '../hooks/useCreditsCheck';

const IDEA_GENERATION_TIMEOUT = 90000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 2000;

interface FetchWithTimeoutOptions {
  timeout: number;
  signal?: AbortSignal;
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit & FetchWithTimeoutOptions
): Promise<Response> {
  const { timeout, signal, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Timeout: Het duurt te lang, probeer opnieuw');
    }
    throw error;
  }
}

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function IdeaGenerate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const creditsInfo = useCreditsCheck(user?.id);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const isGeneratingRef = useRef(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([
    { label: 'Bedrijfsprofiel controleren', status: 'pending' },
    { label: 'Prompt voorbereiden', status: 'pending' },
    { label: 'AI ideeën genereren', status: 'pending' },
    { label: 'Ideeën opslaan', status: 'pending' },
  ]);

  const updateStepStatus = (stepIndex: number, status: ProgressStep['status']) => {
    setProgressSteps(prev => prev.map((step, idx) =>
      idx === stepIndex ? { ...step, status } : step
    ));
  };

  const cleanupOrphanSessions = async () => {
    if (!user) return;

    const twoMinutesAgo = new Date();
    twoMinutesAgo.setMinutes(twoMinutesAgo.getMinutes() - 2);

    const { data: potentialOrphans } = await supabase
      .from('sessions')
      .select('id')
      .eq('user_id', user.id)
      .eq('state', 'idea_selection')
      .lt('created_at', twoMinutesAgo.toISOString());

    if (!potentialOrphans || potentialOrphans.length === 0) return;

    const orphanIds = potentialOrphans.map(s => s.id);
    const { data: ideasForOrphans } = await supabase
      .from('blog_ideas')
      .select('session_id')
      .in('session_id', orphanIds);

    const idsWithIdeas = new Set((ideasForOrphans || []).map(r => r.session_id));
    const toDelete = orphanIds.filter(id => !idsWithIdeas.has(id));

    if (toDelete.length > 0) {
      await supabase.from('sessions').delete().in('id', toDelete);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (isGeneratingRef.current) return;
    if (creditsInfo.isLoading) return;

    const generate = async () => {
      if (isGeneratingRef.current) return;
      isGeneratingRef.current = true;
      setIsGenerating(true);

      let createdSessionId: string | null = null;
      let apiSucceeded = false;

      try {
        if (!creditsInfo.hasCredits) {
          setError('Onvoldoende credits om nieuwe blogs te genereren. Koop extra credits om door te gaan.');
          isGeneratingRef.current = false;
          setIsGenerating(false);
          return;
        }

        updateStepStatus(0, 'in_progress');

        const { data: presentedIdeas } = await supabase
          .from('blog_ideas')
          .select('session_id')
          .eq('user_id', user.id)
          .eq('status', 'presented')
          .limit(1);

        if (presentedIdeas && presentedIdeas.length > 0) {
          const { data: validSession } = await supabase
            .from('sessions')
            .select('id')
            .eq('id', presentedIdeas[0].session_id)
            .eq('state', 'idea_selection')
            .maybeSingle();

          if (validSession?.id) {
            updateStepStatus(0, 'completed');
            updateStepStatus(1, 'completed');
            updateStepStatus(2, 'completed');
            updateStepStatus(3, 'completed');

            await sleep(500);
            navigate(wizardUrl(WIZARD_PATHS.ideaSelect, validSession.id), { replace: true });
            return;
          }
        }

        await cleanupOrphanSessions();

        const twoMinutesAgo = new Date();
        twoMinutesAgo.setMinutes(twoMinutesAgo.getMinutes() - 2);

        const { data: recentSession } = await supabase
          .from('sessions')
          .select('id')
          .eq('user_id', user.id)
          .eq('state', 'idea_selection')
          .gte('created_at', twoMinutesAgo.toISOString())
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        let sessionId: string;

        if (recentSession) {
          sessionId = recentSession.id;
        } else {
          const { data: newSession, error: sessionError } = await supabase
            .from('sessions')
            .insert({ user_id: user.id, state: 'idea_selection' })
            .select()
            .single();

          if (sessionError || !newSession) {
            throw new Error(sessionError?.message || 'Kon geen sessie aanmaken');
          }

          sessionId = newSession.id;
          createdSessionId = sessionId;
        }

        updateStepStatus(0, 'completed');
        updateStepStatus(1, 'in_progress');

        const { data: { session } } = await supabase.auth.getSession();
        const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

        updateStepStatus(1, 'completed');
        updateStepStatus(2, 'in_progress');

        let lastError: Error | null = null;
        let result: any = null;

        for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
          try {
            setRetryAttempt(attempt);

            const response = await fetchWithTimeout(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  type: 'idea',
                  session_id: sessionId,
                  user_id: user.id,
                  data: {},
                }),
                timeout: IDEA_GENERATION_TIMEOUT,
              }
            );

            result = await response.json();

            if (!response.ok) {
              const isInsufficientCredits = response.status === 402 || result.error === 'INSUFFICIENT_CREDITS';
              if (isInsufficientCredits) {
                throw Object.assign(new Error('Onvoldoende credits om nieuwe blogs te genereren. Koop extra credits om door te gaan.'), { isCreditError: true });
              }
              throw new Error(result.error || 'Fout bij het genereren van idee');
            }

            lastError = null;
            break;
          } catch (error) {
            lastError = error as Error;

            if ((error as any).isCreditError) {
              break;
            }

            const errorMessage = error instanceof Error ? error.message : String(error);
            const isTimeout = errorMessage.includes('Timeout');
            const isNetwork = errorMessage.includes('fetch') || errorMessage.includes('network');

            await logGenerationError({
              generationType: 'blog_idea',
              errorCategory: isTimeout ? 'TIMEOUT' : isNetwork ? 'NETWORK_ERROR' : 'API_ERROR',
              errorMessage: errorMessage,
              technicalError: error instanceof Error ? error.stack : undefined,
              retryAttempt: attempt - 1,
              sessionId: sessionId,
              additionalContext: {
                service: 'openai',
                apiType: 'idea_generation',
                timeout: IDEA_GENERATION_TIMEOUT,
                lastError: errorMessage,
                userId: user.id,
              },
            });

            if (attempt < MAX_RETRY_ATTEMPTS) {
              await sleep(RETRY_DELAY);
            }
          }
        }

        if (lastError) {
          if (createdSessionId) {
            try {
              await supabase.from('sessions').delete().eq('id', createdSessionId);
            } catch {
              // swallow cleanup error — user already sees main error
            }
          }

          if ((lastError as any).isCreditError) {
            updateStepStatus(2, 'error');
            throw lastError;
          }

          await logGenerationError({
            generationType: 'blog_idea',
            errorCategory: 'TIMEOUT',
            errorMessage: `Na ${MAX_RETRY_ATTEMPTS} pogingen niet gelukt`,
            technicalError: lastError.stack,
            retryAttempt: MAX_RETRY_ATTEMPTS,
            sessionId: sessionId,
            additionalContext: {
              service: 'openai',
              apiType: 'idea_generation',
              timeout: IDEA_GENERATION_TIMEOUT,
              lastError: lastError.message,
              userId: user.id,
            },
          });

          updateStepStatus(2, 'error');
          throw new Error(
            `Na ${MAX_RETRY_ATTEMPTS} pogingen niet gelukt. Probeer het later opnieuw of neem contact op met support.`
          );
        }

        if (!result || !result.success) {
          if (createdSessionId) {
            try {
              await supabase.from('sessions').delete().eq('id', createdSessionId);
            } catch {
              // swallow cleanup error — user already sees main error
            }
          }
          updateStepStatus(2, 'error');
          throw new Error(result?.error || 'Geen resultaat ontvangen van AI');
        }

        apiSucceeded = true;
        setRetryAttempt(0);
        updateStepStatus(2, 'completed');
        updateStepStatus(3, 'in_progress');

        await sleep(400);

        updateStepStatus(3, 'completed');

        await sleep(500);

        navigate(wizardUrl(WIZARD_PATHS.ideaSelect, sessionId), { replace: true });
      } catch (err: any) {
        if (createdSessionId && !apiSucceeded) {
          try {
            await supabase.from('sessions').delete().eq('id', createdSessionId);
          } catch {
            // swallow cleanup error — user already sees main error
          }
        }

        if (!err.isCreditError) {
          await logGenerationError({
            generationType: 'blog_idea',
            errorCategory: 'UNKNOWN',
            errorMessage: err.message || 'Er ging iets mis bij het genereren',
            technicalError: err instanceof Error ? err.stack : undefined,
            retryAttempt: retryAttempt,
            sessionId: undefined,
            additionalContext: {
              service: 'openai',
              apiType: 'idea_generation',
              timeout: IDEA_GENERATION_TIMEOUT,
              lastError: err instanceof Error ? err.message : String(err),
              userId: user.id,
            },
          });
        }

        setError(err.message || 'Er ging iets mis bij het genereren');
        isGeneratingRef.current = false;
        setIsGenerating(false);
        setRetryAttempt(0);
      }
    };

    generate();
  }, [user?.id, creditsInfo.isLoading, creditsInfo.hasCredits]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Ideeën genereren"
          onBack={() => navigate('/dashboard/new-blog')}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-800 mb-4">{error}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => navigate('/dashboard/new-blog')}
                className="btn-secondary"
              >
                Terug
              </button>
              <button
                onClick={() => {
                  setError(null);
                  isGeneratingRef.current = false;
                  setIsGenerating(false);
                  setProgressSteps([
                    { label: 'Bedrijfsprofiel controleren', status: 'pending' },
                    { label: 'Prompt voorbereiden', status: 'pending' },
                    { label: 'AI ideeën genereren', status: 'pending' },
                    { label: 'Ideeën opslaan', status: 'pending' },
                  ]);
                }}
                className="btn-primary"
              >
                Opnieuw proberen
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Ideeën genereren"
        onBack={() => navigate('/dashboard/new-blog')}
        hideSaveStatus
      />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6 mt-6">
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
              We bedenken ideeën voor je...
            </h1>
            <p className="text-gray-600">
              Een moment geduld, dit kan tot een minuut duren
            </p>
          </div>
          <StepProgressIndicator steps={progressSteps} />
          {retryAttempt > 1 && (
            <p className="text-sm text-gray-500 text-center">
              Poging {retryAttempt} van {MAX_RETRY_ATTEMPTS}...
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
